import * as React from "react";
import { createContext, Dispatch, FC, SetStateAction, useCallback, useContext, useEffect, useState } from "react";
import { getMap } from "../Map/GeoLiteMap";
import { useMapContext } from "./MapContext";
import {
    convertToString,
    coordinateFormat,
    coordToObject,
    IInputCoordinates
} from "../Map/Coordinates/CoordinateUtils";
import { ICoords } from "../Map/Coordinates/CoordinateWidget";
import { getUrlParam } from "../Map/utils";

export interface IConvertContext {
    coordinates: number[];
    setCoordinates: Dispatch<SetStateAction<number[]>>;
    coordinatesString: string;
    setCoordinatesString: Dispatch<SetStateAction<string>>;
    formatType: coordinateFormat;
    setFormatType: Dispatch<SetStateAction<coordinateFormat>>;
    getCurrentFormatCoords: (coords: number[]) => string;
    allCoordinates: ICoords[];
    setAllCoordinates: Dispatch<SetStateAction<ICoords[]>>;
    inputCoordinates: IInputCoordinates;
    setInputCoordinates: Dispatch<SetStateAction<IInputCoordinates>>;
    pinPlaced: boolean;
    setPinPlaced: Dispatch<SetStateAction<boolean>>;
    coordinatesInvalid: boolean;
    setCoordinatesInvalid: Dispatch<SetStateAction<boolean>>;
}

const xArrow = "\u2192 ";
const yArrow = "\u2191 ";

const getPosParam = () => {
    const pos = getUrlParam("pos");
    return pos && pos.split(",").map(Number);
};

const ConvertContext = createContext<IConvertContext>(null);

export const useConvertContext = (): IConvertContext => useContext(ConvertContext);

export const ConvertContextProvider: FC = (props: React.PropsWithChildren<IConvertContext>) => {
    const { configA, placingPin } = useMapContext();

    const getAllCoordinates = (): ICoords[] =>
        configA.coordinateFormats.map((format: string) => {
            const coordinates = convertToString(format, getMap().getView().getCenter());
            const coordFormat =
                format === "LKS"
                    ? format + "92 x(E) " + xArrow + ", y(N) " + yArrow
                    : format === "LKSGEO"
                    ? format.replace("GEO", "") + "92 TM x(N) " + yArrow + ", y(E) " + xArrow
                    : format;
            return { value: format, labelRows: [coordFormat, coordinates] };
        });

    const [coordinates, setCoordinates] = useState<number[]>(getPosParam());
    const [coordinatesString, setCoordinatesString] = useState<string>("");
    const [formatType, setFormatType] = useState<coordinateFormat>("LKS");
    const [allCoordinates, setAllCoordinates] = useState<ICoords[]>(getAllCoordinates);
    const [inputCoordinates, setInputCoordinates] = useState<IInputCoordinates>({ format: "LKS", x: [""], y: [""] });
    const [pinPlaced, setPinPlaced] = useState<boolean>(placingPin);
    const [coordinatesInvalid, setCoordinatesInvalid] = useState<boolean>(false);

    useEffect(() => {
        setCoordinatesString(getCurrentFormatCoords(coordinates));

        if (!coordinates) {
            setInputCoordinates({ format: formatType, x: [""], y: [""] });
            return;
        }

        const all = configA.coordinateFormats.map((format: string) => {
            const coords = convertToString(format, coordinates);
            const coordFormat =
                format === "LKS"
                    ? format + "92 x(E) " + xArrow + ", y(N) " + yArrow
                    : format === "LKSGEO"
                    ? format.replace("GEO", "") + "92 TM x(N) " + yArrow + ", y(E) " + xArrow
                    : format;

            if (format === formatType) {
                const inputCoords = coordToObject(coords, formatType);

                const inputCoord = inputCoords.x
                    ? formatType === "MGRS"
                        ? inputCoords
                        : inputCoords.y
                        ? inputCoords
                        : null
                    : null;
                inputCoord && setInputCoordinates(inputCoord);
            }
            return { value: format, labelRows: [coordFormat, coords] };
        });
        setAllCoordinates(all);
    }, [formatType, coordinates]);

    useEffect(() => {
        pinPlaced && setCoordinatesInvalid(false);
    }, [pinPlaced]);

    // const getCoordinatesString = (feature: Feature<Point>) =>
    //     getCurrentFormatCoords(feature.getGeometry().getCoordinates());

    const getCurrentFormatCoords = useCallback(
        (coords: number[]) => {
            let coordinates = convertToString(formatType, coords);
            if (formatType === "LKS" || formatType === "LKSGEO") {
                const numbers = coordinates.split(",");
                coordinates = numbers[0] + " m, " + numbers[1] + " m";
            }
            return coordinates;
        },
        [formatType]
    );

    const values = {
        coordinates,
        setCoordinates,
        coordinatesString,
        setCoordinatesString,
        formatType,
        setFormatType,
        getCurrentFormatCoords,
        allCoordinates,
        setAllCoordinates,
        inputCoordinates,
        setInputCoordinates,
        pinPlaced,
        setPinPlaced,
        coordinatesInvalid,
        setCoordinatesInvalid
    };

    return <ConvertContext.Provider value={values}>{props.children}</ConvertContext.Provider>;
};
